// Routes, the signed-in guard and the first-run language question.
import { useEffect, useState } from 'preact/hooks';
import { api, getSession, setUnauthorizedHandler } from './api';
import { match, navigate, useLocation } from './router';
import { applyLang, getLang } from './i18n';
import { load, save, KEYS } from './storage';
import { SignIn } from './screens/SignIn';
import { Login } from './screens/Login';
import { Home } from './screens/Home';
import { Albums } from './screens/Albums';
import { Album } from './screens/Album';
import { Timeline } from './screens/Timeline';
import { Search } from './screens/Search';
import { Recipe } from './screens/Recipe';
import { Cook } from './screens/Cook';
import { Grocery } from './screens/Grocery';
import { Settings } from './screens/Settings';
import { LanguagePick } from './screens/LanguagePick';
import { GroceryOrder } from './screens/GroceryOrder';
import { GroceryApps } from './screens/GroceryApps';
import { FoodOrder } from './screens/FoodOrder';
import { InstallPrompt } from './components/InstallPrompt';
import type { Profile } from './types';

const PUBLIC = ['/signin', '/login'];

export function App() {
  const { path } = useLocation();
  const [me, setMe] = useState<Profile | null>(() => load<Profile | null>(KEYS.me, null));
  const session = getSession()?.token ?? '';

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!PUBLIC.includes(location.pathname)) navigate('/signin', { replace: true });
    });
    const l = getLang();
    if (l) applyLang(l);
  }, []);

  // The server is the source of truth for the language (the bot may have set it); a choice made
  // on this device before the account had one is sent up.
  useEffect(() => {
    if (!session) return;
    api.me().then(async (p) => {
      const local = getLang();
      if (p.language) applyLang(p.language);
      else if (local) {
        await api.setLanguage(local).catch(() => undefined);
        p = { ...p, language: local };
      }
      save(KEYS.me, p);
      setMe(p);
    }).catch(() => undefined);
  }, [session]);

  if (path === '/login') return <Login />;
  if (path === '/signin') return session ? <Redirect to="/" /> : <SignIn />;
  if (!session) return <SignIn />;

  if (me && !me.language && !getLang()) {
    return <LanguagePick onDone={(l) => { const p = { ...me, language: l }; save(KEYS.me, p); setMe(p); }} />;
  }

  return <><Screen path={path} /><InstallPrompt /></>;
}

function Screen({ path }: { path: string }) {
  if (path === '/') return <Home />;
  if (path === '/albums') return <Albums />;
  if (path === '/timeline') return <Timeline />;
  if (path === '/search') return <Search />;
  if (path === '/grocery') return <Grocery />;
  if (path === '/grocery/order') return <GroceryApps />;
  if (path === '/settings') return <Settings />;
  let m = match('/grocery/order/:provider', path);
  if (m) return <GroceryOrder key={m.provider} provider={m.provider} />;
  m = match('/order/:provider', path);
  if (m) return <FoodOrder key={m.provider} provider={m.provider} />;
  m = match('/albums/:kind/:key', path);
  if (m && ['craving', 'region', 'meal', 'mine'].includes(m.kind)) return <Album key={path} kind={m.kind} albumKey={m.key} />;
  m = match('/r/:id/cook', path);
  if (m && /^\d+$/.test(m.id)) return <Cook id={Number(m.id)} />;
  m = match('/r/:id', path);
  if (m && /^\d+$/.test(m.id)) return <Recipe key={m.id} id={Number(m.id)} />;
  return <Redirect to="/" />;
}

function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), []);
  return null;
}
